import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { addSample, updateSample } from "./service/sample-service";
import { masterEquipment } from "../ManageEquipment/service/equipment-service";
import { useLoader } from "../../_helper/LoaderContext";

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "36px",
        fontSize: "13px",
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: "0 6px",
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: "#1b255b",
        margin: "2px",
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: "#fff",
        fontWeight: 500,
        fontSize: "12px",
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: "#d32f2f",
        ":hover": {
            backgroundColor: "#ffcdd2",
            color: "white",
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        fontSize: "13px",
    }),
};

const getEquipmentId = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    return item._id || item.equipmentId || item.value || null;
};

const getEquipmentLabel = (item) => {
    if (!item || typeof item === "string") return item || "";
    const name = item.name || "";
    const code = item.code ? ` (${item.code})` : "";
    return `${name}${code}`.trim() || item._id;
};

export default function AddSample({ handleClose, data, getList }) {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const [sample, setSample] = useState("");
    const [error, setError] = useState("");
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState([]);

    useEffect(() => {
        fetchEquipmentMaster();
    }, []);

    useEffect(() => {
        if (data) {
            setSample(data.name || "");
            const existing = data.requiredEquipment || data.requiredEquipments || [];
            const selected = (Array.isArray(existing) ? existing : [])
                .map((item) => {
                    const value = getEquipmentId(item);
                    if (!value) return null;
                    return {
                        value,
                        label: getEquipmentLabel(item) || value,
                    };
                })
                .filter(Boolean);
            setSelectedEquipment(selected);
        } else {
            setSample("");
            setSelectedEquipment([]);
        }
        setError("");
    }, [data]);

    // When master options load, refresh labels for edit mode
    useEffect(() => {
        if (!equipmentOptions.length || !selectedEquipment.length) return;
        setSelectedEquipment((prev) =>
            prev.map((sel) => {
                const match = equipmentOptions.find((opt) => opt.value === sel.value);
                return match || sel;
            })
        );
    }, [equipmentOptions]);

    const fetchEquipmentMaster = async () => {
        try {
            const res = await masterEquipment();
            if (res.data.success === true) {
                const list = res.data.data || [];
                const options = (Array.isArray(list) ? list : []).map((item) => ({
                    value: item._id,
                    label: item.code ? `${item.name} (${item.code})` : item.name,
                }));
                setEquipmentOptions(options);
            }
        } catch (err) {
            console.log(err);
        }
    };

    const handleSubmit = async () => {
        if (!sample.trim()) {
            setError(t("Please enter sample"));
            return;
        }

        const form_data = new FormData();
        form_data.append("name", sample.trim());
        if (data) {
            form_data.append("sampleId", data._id);
        }
        selectedEquipment.forEach((item) => {
            form_data.append("requiredEquipment[]", item.value);
        });

        setLoading(true);
        try {
            const res = data
                ? await updateSample(form_data)
                : await addSample(form_data);

            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                setError("");
                handleClose();
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal.Body className={data ? "py-2 px-3" : "pt-1 pb-2 px-3"}>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <Form.Group className="mb-2" controlId="sample">
                        <Form.Label className="form-label mb-1" style={{ fontSize: "12.5px", fontWeight: 500 }}>
                            {t("Sample Name")} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            size="sm"
                            placeholder={t("Enter sample name")}
                            value={sample}
                            onChange={(e) => {
                                setSample(e.target.value);
                                setError("");
                            }}
                            isInvalid={!!error}
                            style={{ height: "36px", fontSize: "13px" }}
                        />
                        {error && (
                            <Form.Control.Feedback type="invalid" style={{ fontSize: "11px", marginTop: "2px" }}>
                                {error}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <Form.Group controlId="requiredEquipment" className="mb-0">
                        <Form.Label className="form-label mb-1" style={{ fontSize: "12.5px", fontWeight: 500 }}>
                            {t("Required Equipment")}
                        </Form.Label>
                        <Select
                            isMulti
                            isSearchable
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={equipmentOptions}
                            value={selectedEquipment}
                            onChange={(selected) => setSelectedEquipment(selected || [])}
                            placeholder={t("Select equipment...")}
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="py-2 px-3 border-top d-flex justify-content-end gap-2">
                <Button variant="primary" size="sm" className="px-3" onClick={handleSubmit}>
                    {data ? t("Update") : t("Create")}
                </Button>
                <Button variant="secondary" size="sm" className="px-3" onClick={handleClose}>
                    {t("Close")}
                </Button>
            </Modal.Footer>
        </>
    );
}
